import type { RawEntityType } from '@/types/entity-data';

export const formatNumber = (val: number | string | undefined | null): string => {
    if (val === undefined || val === null) return '0';
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return new Intl.NumberFormat('pt-BR').format(num);
};

/** Count for table cells: formatted when positive, blank when zero/missing (an empty cell, not "0" or "—"). */
export const formatCountOrBlank = (val: number | null | undefined): string => (val ? formatNumber(val) : '');

/** A count with its noun in singular or plural, e.g. "1 empresa", "1.508 empresas". */
export const formatCountWithNoun = (count: number, singular: string, plural: string): string =>
  `${formatNumber(count)} ${count === 1 ? singular : plural}`;

/** Rich-editor HTML as plain text, block breaks kept as line breaks. Parsed inertly: nothing runs. */
export const htmlToPlainText = (html: string): string => {
  const withBreaks = html.replace(/<br\s*\/?>|<\/(p|div|li|h[1-6])>/gi, '$&\n');
  const text = new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent ?? '';
  return text.replace(/[ \t]+/g, ' ').replace(/\n\s*\n+/g, '\n').trim();
};

// `part` as a 0..100 percentage of `total`, clamped to 100 and guarded against a
// zero/absent total (→ 0). Numeric (not formatted) — for widths, gauges, share bars.
export const percentageOf = (part: number, total: number): number =>
  total > 0 ? Math.min(100, (part / total) * 100) : 0;

/** `part` as a formatted share of `total` ("12.5%"); a non-zero share too small to show as 0.1% reads "<0.1%"
 *  rather than "0%". "—" without a total. */
export const formatSharePercent = (part: number, total: number): string => {
  if (total <= 0) return '—';
  const share = percentageOf(part, total);
  return share > 0 && share < 0.1 ? '<0.1%' : formatPercentNoPrefix(share);
};

// One decimal place, but a trailing ".0" is dropped so round values read as "100%", not
// "100.0%", while fractional values keep their decimal ("81.3%").
const toTrimmedFixed1 = (num: number): string => {
  const fixed = num.toFixed(1);
  return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
};

export const formatPercent = (val: number | string | null | undefined): string => {
  if (val === undefined || val === null) return '--';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  const prefix = num > 0 ? '+' : '';
  return `${prefix}${toTrimmedFixed1(num)}%`;
};

// Same format as formatPercent, minus the '+' prefix: formatPercent prefixes
// positives because it formats deltas, which is wrong for a plain "% of total" share.
export const formatPercentNoPrefix = (val: number | string | null | undefined): string => {
  if (val === undefined || val === null) return '--';
  const num = typeof val === 'string' ? parseFloat(val) : val;
  return Number.isNaN(num) ? '--' : `${toTrimmedFixed1(num)}%`;
};

const parseNumericValue = (value: string | number): number => {
    if (typeof value === 'number') {
        return value;
    }

    const sanitizedValue = value.trim().replace(/[^\d,.-]/g, '');
    if (!sanitizedValue) {
        return Number.NaN;
    }

    const dotMatches = sanitizedValue.match(/\./g) ?? [];
    const hasComma = sanitizedValue.includes(',');

    if (hasComma) {
        if (dotMatches.length > 0) {
            if (sanitizedValue.lastIndexOf(',') > sanitizedValue.lastIndexOf('.')) {
                return Number(sanitizedValue.replace(/\./g, '').replace(',', '.'));
            }

            return Number(sanitizedValue.replace(/,/g, ''));
        }

        return Number(sanitizedValue.replace(',', '.'));
    }

    if (dotMatches.length > 1) {
        return Number(sanitizedValue.replace(/\./g, ''));
    }

    if (dotMatches.length === 1) {
        const [integerPart = '', fractionalPart = ''] = sanitizedValue.split('.');
        if (integerPart.length <= 3 && fractionalPart.length === 3) {
            return Number(`${integerPart}${fractionalPart}`);
        }
    }

    return Number(sanitizedValue);
};

export const formatCurrency = (value: string | number | null | undefined, currency: string = 'BRL'): string => {
    if (value === undefined || value === null) return 'N/A';

    const numericValue = parseNumericValue(value);
    if (Number.isNaN(numericValue)) {
        return value.toString();
    }

    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: currency
    }).format(numericValue);
};

export const formatLargeCurrency = (value: string | number | null | undefined): string => {
    if (value === undefined || value === null) return 'N/A';

    const numericValue = parseNumericValue(value);
    if (Number.isNaN(numericValue)) return value.toString();

    if (numericValue >= 1_000_000_000) {
        return (numericValue / 1_000_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' Bi';
    }
    if (numericValue >= 1_000_000) {
        return (numericValue / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' Mi';
    }
    
    return formatCurrency(value);
};

const compactCurrencyFormatter = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
});

// "R$ 8,2 mil – R$ 11,5 mil", "Até R$ 100 mil" or "Acima de R$ 5 mi"; a null bound is open.
export const formatMoneyRange = (range: { min: number | null; max: number | null } | null | undefined): string | null => {
    if (!range || (range.min === null && range.max === null)) return null;

    if (range.min === null) return `Até ${compactCurrencyFormatter.format(range.max!)}`;
    if (range.max === null) return `Acima de ${compactCurrencyFormatter.format(range.min)}`;
    return `${compactCurrencyFormatter.format(range.min)} – ${compactCurrencyFormatter.format(range.max)}`;
};

export const formatLargeNumber = (value: string | number | null | undefined): string => {
    if (value === undefined || value === null) return 'N/A';

    const numericValue = parseNumericValue(value);
    if (Number.isNaN(numericValue)) return value.toString();

    if (numericValue >= 1_000_000_000) {
        return (numericValue / 1_000_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' Bi';
    }
    if (numericValue >= 1_000_000) {
        return (numericValue / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) + ' Mi';
    }

    return formatNumber(numericValue);
};

export const formatCnae = (code: string | undefined): string => {
    if (!code || code.length !== 7) return code || '';
    return `${code.substring(0, 4)}-${code.substring(4, 5)}/${code.substring(5, 7)}`;
};

export function formatDate(
  date: string | Date | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  fallback = 'N/A',
  locale = 'pt-BR'
): string {
  if (!date) {
    return fallback;
  }

  // Backward-compatible behavior for date-only formatter
  if (!options && typeof date === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
      return date;
  }

  try {
    let dateStr: string | Date = date;

    if (typeof date === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        dateStr = `${date}T00:00:00`;
      } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(date)) {
        dateStr = `${date}Z`;
      }
    }

    const parsed = new Date(dateStr);

    if (Number.isNaN(parsed.getTime())) {
      return fallback;
    }

    if (!options) {
      return parsed.toLocaleDateString(locale);
    }

    return parsed.toLocaleString(locale, options);
  } catch (e) {
    console.error("Invalid date provided to formatDate:", e);
    return options ? fallback : '';
  }
}

/**
 * Whole days from `start` to `end`, inclusive of both endpoints (so a same-day
 * range is 1). Returns null when either date is missing/unparseable.
 */
export function daysBetween(
  start: string | Date | null | undefined,
  end: string | Date | null | undefined,
): number | null {
  if (!start || !end) return null;
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null;
  return Math.round((endMs - startMs) / 86_400_000) + 1;
}

export function formatDateTime(
  date: string | Date | null | undefined,
  fallback = 'N/A',
  locale = 'pt-BR'
): string {
  return formatDate(
    date,
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    fallback,
    locale,
  );
}

/** Date formatted as "27 abr 2026" (compact month). Returns `fallback` when missing/invalid. */
export function formatShortDate(
  date: string | Date | null | undefined,
  fallback = '—',
  locale = 'pt-BR'
): string {
  return formatDate(
    date,
    { day: '2-digit', month: 'short', year: 'numeric' },
    fallback,
    locale,
  );
}

/** Date formatted as "27 abr 2026, 14:30" (compact month + time). */
export function formatShortDateTime(
  date: string | Date | null | undefined,
  fallback = '—',
  locale = 'pt-BR'
): string {
  return formatDate(
    date,
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    },
    fallback,
    locale,
  );
}

export function formatFoundingDate(date: string | Date | null | undefined): string {
    if (!date) return 'N/A';
    
    const d = new Date(typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date + 'T00:00:00' : date);
    if (isNaN(d.getTime())) return typeof date === 'string' ? date : 'N/A';

    const now = new Date();
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(now.getFullYear() - 2);

    if (d > twoYearsAgo) {
        return d.toLocaleDateString('pt-BR');
    }
    return d.getFullYear().toString();
}

export function normalizeCpf(cpf: string | null | undefined): string {
  if (!cpf) {
    return '';
  }

  return String(cpf).replace(/\D/g, '');
}

export function normalizeCnpj(cnpj: string | null | undefined): string {
  if (!cnpj) {
    return '';
  }

  return String(cnpj).toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function normalizeTaxId(taxId: string | null | undefined): string {
  const cleaned = normalizeCnpj(taxId);

  if (cleaned.length === 11) {
    return normalizeCpf(taxId);
  }

  return cleaned;
}

// A Brazilian tax id with 11 digits is a CPF (person); anything else (CNPJ root of 8
// digits, full CNPJ of 14) is a company. The digit count is the only signal.
export function entityTypeFromTaxId(taxId: string | null | undefined): RawEntityType {
  return (taxId ?? '').replace(/\D/g, '').length === 11 ? 'person' : 'company';
}

export function formatTaxId(taxId: string | null | undefined): string {
  if (!taxId) {
    return '';
  }

  const cleaned = normalizeTaxId(taxId);

  if (cleaned.length === 8) {
    // Format as CNPJ base (XX.XXX.XXX)
    return cleaned.replace(/([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})/, '$1.$2.$3');
  }

  if (cleaned.length === 11) {
    // Format as CPF (XXX.XXX.XXX-XX)
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  if (cleaned.length === 14) {
    // Format as full CNPJ (XX.XXX.XXX/XXXX-XX)
    return cleaned.replace(/([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  return String(taxId); // Return original as a string if no format matches
}

/** CNPJ branch/order code (digits 9–12): e.g. "0001" for matriz. */
export function formatCnpjBranchCode(taxId: string | null | undefined): string {
  const cleaned = normalizeTaxId(taxId);
  if (cleaned.length < 12) return cleaned || '—';
  return cleaned.slice(8, 12);
}

export function getInitials(text: string | null | undefined): string{
  if(!text) return ''
  const words = text.split(' ');
  if (words.length > 1) {
    return ((words[0]?.[0] ?? '') +(words[1]?.[0] ?? '')).toUpperCase();
  }
  return (words[0]?.[0] || '').toUpperCase();
}

export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Formats bytes using decimal (SI) units (1 KB = 1000 bytes).
 * Matches how macOS/Windows display file sizes and aligns with upload limits.
 */
export function formatFileSize(bytes: number | null | undefined, decimals = 1): string {
  if (bytes == null) return '—';
  if (bytes === 0) return '0 B';

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
